/**
 * Zobrist hashing for the transposition table. Two independent hashes are kept:
 * `h1` keys the table, `h2` verifies entries so a rare `h1` collision cannot
 * feed a wrong score into the search.
 *
 * A tiny seeded PRNG (mulberry32) generates the keys so hashing is deterministic
 * across runs without relying on Math.random.
 */

import { Color, PieceType, type Move, type Position } from "@xq/rules";

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function rand31(rng: () => number): number {
  return (rng() * 0x7fffffff) | 0;
}

// pieceIndex = color * 7 + type, 14 piece kinds x 90 squares.
const Z1: number[][] = [];
const Z2: number[][] = [];
const rng1 = mulberry32(0x1a2b3c4d);
const rng2 = mulberry32(0x5e6f7a8b);
for (let pc = 0; pc < 14; pc++) {
  Z1[pc] = [];
  Z2[pc] = [];
  for (let sq = 0; sq < 90; sq++) {
    Z1[pc]![sq] = rand31(rng1);
    Z2[pc]![sq] = rand31(rng2);
  }
}
const SIDE1 = rand31(rng1);
const SIDE2 = rand31(rng2);

function pieceIndex(color: Color, type: PieceType): number {
  return color * 7 + type;
}

export interface Hash {
  h1: number;
  h2: number;
}

export function fullHash(pos: Position): Hash {
  let h1 = 0,
    h2 = 0;
  for (let sq = 0; sq < 90; sq++) {
    const p = pos.board[sq];
    if (!p) continue;
    const pc = pieceIndex(p.color, p.type);
    h1 ^= Z1[pc]![sq]!;
    h2 ^= Z2[pc]![sq]!;
  }
  if (pos.sideToMove === Color.Black) {
    h1 ^= SIDE1;
    h2 ^= SIDE2;
  }
  return { h1, h2 };
}

/**
 * Update a hash for a move, given the moving piece and any captured piece.
 * Call before flipping side-to-move logic; it toggles the side key itself.
 */
export function updateHash(
  hash: Hash,
  move: Move,
  moverColor: Color,
  moverType: PieceType,
  captured: { color: Color; type: PieceType } | null,
): void {
  const mpc = pieceIndex(moverColor, moverType);
  hash.h1 ^= Z1[mpc]![move.from]! ^ Z1[mpc]![move.to]!;
  hash.h2 ^= Z2[mpc]![move.from]! ^ Z2[mpc]![move.to]!;
  if (captured) {
    const cpc = pieceIndex(captured.color, captured.type);
    hash.h1 ^= Z1[cpc]![move.to]!;
    hash.h2 ^= Z2[cpc]![move.to]!;
  }
  hash.h1 ^= SIDE1;
  hash.h2 ^= SIDE2;
}
